/**
 * PolygonMap Component
 *
 * Interactive map showing all polygons with:
 * - Color-coded polygons by commodity
 * - Click popups with polygon details
 * - Multiple base layers (satellite, streets, hybrid)
 * - Overlap visualization layer
 * - Fit bounds to all polygons
 */

import { useEffect, useRef, useCallback, ReactNode } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as turf from '@turf/turf';

interface PolygonFeature {
    type: 'Feature';
    geometry: GeoJSON.Geometry;
    properties: {
        id: string;
        name: string;
        producer_name?: string;
        commodity: string;
        area_ha?: number;
        country?: string;
        region?: string;
        status: string;
    };
}

interface OverlapFeature {
    polygon_a_id: string;
    polygon_a_name: string;
    polygon_b_id: string;
    polygon_b_name: string;
    overlap_area_ha: number;
    overlap_percentage_a: number;
    overlap_percentage_b: number;
    overlap_geometry: GeoJSON.Geometry;
}

interface PolygonMapProps {
    features: PolygonFeature[];
    overlaps?: OverlapFeature[];
    selectedPolygonId?: string | null;
    onPolygonClick?: (polygonId: string) => void;
    showOverlaps?: boolean;
    height?: string;
    bufferPreview?: GeoJSON.Geometry | null;
    simplifyPreview?: GeoJSON.Geometry | null;
    highlightedPolygonIds?: string[];
    onMapReady?: (map: L.Map) => void;
    children?: React.ReactNode;
}

const COMMODITY_COLORS: Record<string, string> = {
    cocoa: '#8B4513',
    coffee: '#6F4E37',
    palm_oil: '#FF8C00',
    soy: '#9ACD32',
    timber: '#228B22',
    rubber: '#2F4F4F',
    cattle: '#DAA520',
};

const DEFAULT_COLOR = '#3388ff';

function getCommodityColor(commodity: string): string {
    return COMMODITY_COLORS[commodity] || DEFAULT_COLOR;
}

export default function PolygonMap({
    features,
    overlaps = [],
    selectedPolygonId,
    onPolygonClick,
    showOverlaps = false,
    height = '600px',
    bufferPreview = null,
    simplifyPreview = null,
    highlightedPolygonIds = [],
    onMapReady,
}: PolygonMapProps) {
    const mapRef = useRef<L.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const polygonLayerRef = useRef<L.GeoJSON | null>(null);
    const overlapLayerRef = useRef<L.GeoJSON | null>(null);
    const selectedLayerRef = useRef<L.GeoJSON | null>(null);
    const bufferLayerRef = useRef<L.GeoJSON | null>(null);
    const simplifyLayerRef = useRef<L.GeoJSON | null>(null);

    // Stable refs for callbacks/values that should NOT trigger layer rebuilds
    const onPolygonClickRef = useRef(onPolygonClick);
    onPolygonClickRef.current = onPolygonClick;
    const highlightedIdsRef = useRef(highlightedPolygonIds);
    highlightedIdsRef.current = highlightedPolygonIds;

    // Initialize map once
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const map = L.map(mapContainerRef.current, {
            center: [-1.8312, -78.1834],
            zoom: 7,
            zoomControl: true,
        });

        const esriSatellite = L.tileLayer(
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            { attribution: '&copy; Esri, Maxar, Earthstar Geographics', maxZoom: 19 }
        );

        const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19,
        });

        const googleHybrid = L.tileLayer(
            'https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
            { attribution: '&copy; Google Hybrid', subdomains: ['0', '1', '2', '3'], maxZoom: 20 }
        );

        esriSatellite.addTo(map);

        L.control
            .layers(
                { 'Satélite (Esri)': esriSatellite, 'Google Hybrid': googleHybrid, OpenStreetMap: osm },
                {},
                { position: 'topleft' }
            )
            .addTo(map);

        mapRef.current = map;
        onMapReady?.(map);

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);

    // Helper: compute style for a polygon feature given current selection state
    const getPolygonStyle = useCallback(
        (feature: GeoJSON.Feature | undefined, selId: string | null | undefined, hlIds: string[]) => {
            const commodity = feature?.properties?.commodity || '';
            const isSelected = feature?.properties?.id === selId;
            const isHighlighted = hlIds.includes(feature?.properties?.id);
            return {
                color: isSelected ? '#ffffff' : isHighlighted ? '#f59e0b' : getCommodityColor(commodity),
                weight: isSelected ? 3 : isHighlighted ? 3 : 2,
                opacity: 1,
                fillColor: isHighlighted ? '#f59e0b' : getCommodityColor(commodity),
                fillOpacity: isSelected ? 0.5 : isHighlighted ? 0.35 : 0.25,
            };
        },
        []
    );

    // Update polygon layer ONLY when features data changes (NOT on selection/callback changes)
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Remove old layer
        if (polygonLayerRef.current) {
            map.removeLayer(polygonLayerRef.current);
        }

        if (!features.length) return;

        const geojsonData: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: features as any,
        };

        const layer = L.geoJSON(geojsonData, {
            style: (feature) => getPolygonStyle(feature, undefined, highlightedIdsRef.current),
            onEachFeature: (feature, featureLayer) => {
                const p = feature.properties;
                const areaText = p.area_ha ? `${p.area_ha} ha` : 'N/A';
                const location = [p.region, p.country].filter(Boolean).join(', ');

                featureLayer.bindPopup(
                    `<div style="min-width:180px">
                        <strong style="font-size:14px">${p.name || 'Sin nombre'}</strong>
                        ${p.producer_name ? `<br><span style="color:#666">${p.producer_name}</span>` : ''}
                        <hr style="margin:6px 0;border:none;border-top:1px solid #eee">
                        <div style="font-size:12px">
                            <div><strong>Área:</strong> ${areaText}</div>
                            <div><strong>Commodity:</strong> ${p.commodity || 'N/A'}</div>
                            ${location ? `<div><strong>Ubicación:</strong> ${location}</div>` : ''}
                        </div>
                    </div>`,
                    { maxWidth: 300 }
                );

                featureLayer.on('click', () => {
                    if (onPolygonClickRef.current && p.id) {
                        onPolygonClickRef.current(p.id);
                    }
                });
            },
        });

        layer.addTo(map);
        polygonLayerRef.current = layer;

        // Fit bounds to show all polygons
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
        }
    }, [features, getPolygonStyle]);

    // Update polygon styles in-place when selection changes (no layer rebuild)
    useEffect(() => {
        const layer = polygonLayerRef.current;
        if (!layer) return;

        layer.eachLayer((l) => {
            const geoLayer = l as L.GeoJSON;
            const feature = (geoLayer as any).feature as GeoJSON.Feature | undefined;
            if (feature && 'setStyle' in geoLayer) {
                (geoLayer as L.Path).setStyle(
                    getPolygonStyle(feature, selectedPolygonId, highlightedPolygonIds)
                );
            }
        });
    }, [selectedPolygonId, highlightedPolygonIds, getPolygonStyle]);

    // Update overlap layer
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (overlapLayerRef.current) {
            map.removeLayer(overlapLayerRef.current);
        }

        if (!showOverlaps || !overlaps.length) return;

        const overlapFeatures: GeoJSON.Feature[] = overlaps.map((o) => ({
            type: 'Feature' as const,
            geometry: o.overlap_geometry,
            properties: {
                polygon_a_name: o.polygon_a_name,
                polygon_b_name: o.polygon_b_name,
                overlap_area_ha: o.overlap_area_ha,
            },
        }));

        const overlapCollection: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: overlapFeatures,
        };

        const layer = L.geoJSON(overlapCollection, {
            style: {
                color: '#ef4444',
                weight: 2,
                opacity: 0.9,
                fillColor: '#ef4444',
                fillOpacity: 0.4,
                dashArray: '5, 5',
            },
            onEachFeature: (feature, featureLayer) => {
                const p = feature.properties;
                featureLayer.bindPopup(
                    `<div style="min-width:160px">
                        <strong style="color:#ef4444">Superposición</strong>
                        <hr style="margin:4px 0;border:none;border-top:1px solid #eee">
                        <div style="font-size:12px">
                            <div>${p.polygon_a_name}</div>
                            <div>× ${p.polygon_b_name}</div>
                            <div style="margin-top:4px"><strong>${p.overlap_area_ha} ha</strong></div>
                        </div>
                    </div>`
                );
            },
        });

        layer.addTo(map);
        overlapLayerRef.current = layer;
    }, [overlaps, showOverlaps]);

    // Buffer preview layer
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (bufferLayerRef.current) {
            map.removeLayer(bufferLayerRef.current);
            bufferLayerRef.current = null;
        }

        if (!bufferPreview) return;

        const feature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: bufferPreview,
            properties: {},
        };

        const layer = L.geoJSON(feature, {
            style: {
                color: '#3b82f6',
                weight: 2,
                opacity: 0.8,
                fillColor: '#3b82f6',
                fillOpacity: 0.15,
                dashArray: '8, 4',
            },
        });

        layer.addTo(map);
        bufferLayerRef.current = layer;
    }, [bufferPreview]);

    // Simplify preview layer
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (simplifyLayerRef.current) {
            map.removeLayer(simplifyLayerRef.current);
            simplifyLayerRef.current = null;
        }

        if (!simplifyPreview) return;

        const feature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: simplifyPreview,
            properties: {},
        };

        const layer = L.geoJSON(feature, {
            style: {
                color: '#a855f7',
                weight: 2,
                opacity: 0.8,
                fillColor: '#a855f7',
                fillOpacity: 0.15,
                dashArray: '6, 3',
            },
        });

        layer.addTo(map);
        simplifyLayerRef.current = layer;
    }, [simplifyPreview]);

    // Pan to selected polygon (direct transition, no parabolic zoom-out)
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !selectedPolygonId || !features.length) return;

        const selected = features.find((f) => f.properties.id === selectedPolygonId);
        if (!selected) return;

        try {
            const bounds = L.geoJSON(selected as any).getBounds();
            if (bounds.isValid()) {
                // Use fitBounds with animate — moves directly to the target
                // without the flyTo parabolic zoom-out/zoom-in
                map.fitBounds(bounds, {
                    padding: [80, 80],
                    maxZoom: 16,
                    animate: true,
                    duration: 0.8,
                });
            }
        } catch {
            // ignore
        }
    }, [selectedPolygonId, features]);

    return (
        <div className="relative" style={{ height }}>
            <div
                ref={mapContainerRef}
                className="w-full h-full rounded-lg border border-gray-200 dark:border-gray-700"
                style={{ background: '#2d3436' }}
            />

            {/* Legend */}
            {features.length > 0 && (
                <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 sm:p-3 z-[1000] max-w-[140px] sm:max-w-[200px]">
                    <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
                        Commodities
                    </h4>
                    <div className="space-y-1">
                        {Object.entries(COMMODITY_COLORS).map(([key, color]) => {
                            const count = features.filter(
                                (f) => f.properties.commodity === key
                            ).length;
                            if (count === 0) return null;
                            return (
                                <div key={key} className="flex items-center gap-2 text-xs">
                                    <div
                                        className="w-4 h-3 rounded-sm border border-gray-300"
                                        style={{ backgroundColor: color }}
                                    />
                                    <span className="text-gray-700 dark:text-gray-300 capitalize">
                                        {key.replace('_', ' ')} ({count})
                                    </span>
                                </div>
                            );
                        })}
                        {showOverlaps && overlaps.length > 0 && (
                            <div className="flex items-center gap-2 text-xs pt-1 border-t border-gray-200 dark:border-gray-700">
                                <div className="w-4 h-3 rounded-sm bg-red-500 border border-red-600" />
                                <span className="text-red-600 font-medium">
                                    Superposiciones ({overlaps.length})
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Stats bar */}
            {features.length > 0 && (
                <div className="absolute top-2 right-12 sm:top-3 sm:right-14 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg shadow px-2 py-1 sm:px-3 sm:py-1.5 z-[1000]">
                    <span className="text-xs text-gray-700 dark:text-gray-300">
                        {features.length} polígonos •{' '}
                        {features
                            .reduce((sum, f) => sum + (f.properties.area_ha || 0), 0)
                            .toFixed(1)}{' '}
                        ha total
                    </span>
                </div>
            )}
        </div>
    );
}
